import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import Button from '../../../../button';
import Modalheader from '../../../components/header';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import useModal from '../../../useModal';
import UseContext from '../../../../../hooks/useContext';
import ApiDatabase from 'server';
import StyledInput from '../../../../styledInput/StyledInput';
import StepPills from '../../../../stepPills/StepPills';
import CheckboxOptionCard from '../../../../checkboxOptionCard/CheckboxOptionCard';
import { parseCalendarDate } from '../../../../../helper/helper';
import {
  stepsFor,
  canSubmit,
  defaultInterruptionDate,
  interruptionDateError,
  duplicateDateErrors,
  defaultDuplicateDates,
  asksPresence,
  presenceAnswer,
  reasonsForRequester,
  requestersFor,
  cancellationErrorMessage,
} from '../../../../../helper/missionCancellation';
import { Calendar, Check, CrossIcon, WarningCircle } from '../../../../../assets/icons';

registerLocale('fr-FR', fr);

// Who can be the origin of the cancellation, in the select's order. Which of
// them the mission actually allows is the helper's call (requestersFor).
const REQUESTER_LABELS = {
  tempworker: 'Intérimaire',
  company: 'Entreprise',
  admin: 'Admin',
};

const PRESENCE_OPTIONS = [
  { value: 'present', label: 'Oui', Icon: Check },
  { value: 'absent', label: 'Non', Icon: CrossIcon },
];

// Civil day 'YYYY-MM-DD': the only format the api accepts for the payload's
// dates. Plain formatting (twin of calendarDayOf in
// ModalMissionDetailSubmit.js) — the validation itself stays in the helper.
function calendarDayOf(value) {
  const date = parseCalendarDate(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Date field, copied identically from ModalMissionInterrupt.js.
function DateField({ label, value, onChange, minDate, maxDate, error }) {
  return (
    <label className="ft-sm text-gray-500 block">
      {label}
      <div className="relative mt-1">
        <DatePicker
          dateFormat="dd/MM/yyyy"
          locale="fr-FR"
          selected={value}
          onChange={onChange}
          minDate={minDate}
          maxDate={maxDate}
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          popperPlacement="auto"
          popperProps={{ strategy: 'fixed' }}
          popperClassName="z-[9999]"
          className="w-full h-10 pl-10 pr-3 ft-b border rounded-lg text-left text-[14px] cursor-pointer"
        />
        <div className="absolute inset-y-0 left-0 flex items-center px-2.5 pointer-events-none">
          <Calendar wh={18} color="#6B7280" />
        </div>
      </div>
      {error && (
        <div className="text-red-500 flex items-center gap-1 text-xs mt-1">
          <WarningCircle wh={16} color="#EF4444" />
          {error}
        </div>
      )}
    </label>
  );
}

export default function ModalMissionCancellation() {
  const { toggle } = useModal();
  const { objID, setModalDataReturn, updateMissionStatusLocal } = UseContext();
  const token = localStorage.getItem('xsrfToken');

  const [mission, setMission] = useState(null);
  const [isWaiting, setIsWaiting] = useState(true);
  const [reasons, setReasons] = useState([]);
  const [step, setStep] = useState('Annulation');
  const [requester, setRequester] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [comment, setComment] = useState('');
  const [presence, setPresence] = useState(null);
  const [keepMission, setKeepMission] = useState(true);
  // Left empty until the admin picks one: the default depends on the mission,
  // which is still loading, so it is derived below rather than stored here.
  const [interruptionDate, setInterruptionDate] = useState(null);
  const [replace, setReplace] = useState(false);
  const [duplicateDateStart, setDuplicateDateStart] = useState(null);
  const [duplicateDateEnd, setDuplicateDateEnd] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!objID) return;
    setIsWaiting(true);
    ApiDatabase.getMissionDetailsById(
      { idMission: objID },
      (data) => setMission(data),
      () => {
        toast.error('Erreur lors du chargement de la mission');
        setIsWaiting(false);
      },
      { skipCache: true }
    );
  }, [objID]);

  useEffect(() => {
    if (!mission) return;
    // A single call: the modal's new type and the old interruption list,
    // which getReasonsMissionSuspension combines via a comma-separated
    // list of types.
    ApiDatabase.getReasonsMissionSuspension(
      { type: 'cancellation_modal,interruption' },
      (data) => {
        setReasons(data || []);
        setIsWaiting(false);
      },
      () => setIsWaiting(false)
    );
  }, [mission]);

  const offeredRequesters = useMemo(
    () =>
      requestersFor(mission).map((value) => ({
        value,
        label: REQUESTER_LABELS[value],
      })),
    [mission]
  );

  const offeredReasons = useMemo(
    () => reasonsForRequester(reasons, requester, mission),
    [reasons, requester, mission]
  );

  // « Intérimaire » chosen, then the mission turns out to have none: the
  // answer must not survive as a value the select no longer shows.
  useEffect(() => {
    if (requester && !offeredRequesters.some((option) => option.value === requester)) {
      setRequester('');
    }
  }, [offeredRequesters, requester]);

  // A motif no longer offered after a change of « à la demande » must not
  // stay silently selected.
  useEffect(() => {
    if (reasonId && !offeredReasons.some((reason) => reason._id === reasonId)) {
      setReasonId('');
    }
  }, [offeredReasons, reasonId]);

  // Default values for the duplicated mission's dates (Dates step) as long
  // as the admin has not touched them — provided by the helper, not
  // recomputed here.
  // Today, brought back inside the mission's period (helper): a past mission
  // otherwise opened on a date its own rule refuses.
  const effectiveInterruptionDate =
    interruptionDate ?? defaultInterruptionDate({ mission });

  /** A boundary for the picker, or nothing while the mission is still loading. */
  const boundary = (value) => {
    if (!value) return undefined;
    const date = parseCalendarDate(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  };

  const duplicateDefaults = defaultDuplicateDates({
    mission,
    interruptionDate: effectiveInterruptionDate,
  });
  const effectiveDuplicateStart =
    duplicateDateStart ?? parseCalendarDate(duplicateDefaults.start);
  const effectiveDuplicateEnd = duplicateDateEnd ?? parseCalendarDate(duplicateDefaults.end);

  const helperState = {
    mission,
    requester,
    presence,
    replace,
    reasonId,
    interruptionDate: effectiveInterruptionDate,
    duplicateDateStart: effectiveDuplicateStart,
    duplicateDateEnd: effectiveDuplicateEnd,
  };

  // Upcoming mission: nobody could have shown up, the step reduces to the
  // « Conserver la mission » checkbox (spec §2) and the answer goes out as
  // « absent ».
  const presenceAsked = asksPresence(helperState);
  const effectivePresence = presenceAnswer(helperState);

  const steps = stepsFor(helperState);
  // The displayed step, brought back to the last still-valid step if the
  // one in memory has disappeared (e.g. replacement unchecked after going
  // back) — derived rather than through an effect, so as never to render
  // a body that no longer matches the current steps.
  const currentStep = steps.includes(step) ? step : steps[steps.length - 1];
  const stepPills = steps.map((label) => ({ key: label, label }));
  const isLastStep = steps[steps.length - 1] === currentStep;
  const dateError = interruptionDateError(helperState);
  const dupErrors = duplicateDateErrors(helperState);

  // What only the displayed step requires: lets « Suivant » activate
  // without waiting on the fields of a step not shown yet, which
  // canSubmit — itself — looks at all together. canSubmit remains the
  // sole authority for Confirmer.
  const stepReady = (() => {
    if (currentStep === 'Annulation') return Boolean(requester) && Boolean(reasonId);
    if (currentStep === 'Présence') {
      if (!presenceAsked) return true;
      return presence === 'present' ? !dateError : Boolean(presence);
    }
    return true;
  })();

  const ready = isLastStep ? canSubmit(helperState) : stepReady;

  const goNext = () => {
    const index = steps.indexOf(currentStep);
    if (index < steps.length - 1) setStep(steps[index + 1]);
  };

  const handleConfirm = () => {
    setIsSubmitting(true);
    const showedUp = effectivePresence === 'present';
    ApiDatabase.missionCancellation(
      {
        token,
        idMission: objID,
        requester,
        reasonId,
        comment,
        presence: effectivePresence,
        keepMission,
        interruptionDate: showedUp ? calendarDayOf(effectiveInterruptionDate) : null,
        // No replacement possible without presence: otherwise a `replace` left
        // true in memory (after switching back from « Oui » to « Non »)
        // would go out alone, without the dates that go with it — the api
        // currently only gets away with it by luck (it re-derives
        // `showedUp && replace` on its own side), not by design.
        replace: showedUp && replace,
        duplicateDateStart:
          showedUp && replace ? calendarDayOf(effectiveDuplicateStart) : null,
        duplicateDateEnd: showedUp && replace ? calendarDayOf(effectiveDuplicateEnd) : null,
      },
      (data) => {
        const reasonName = reasons.find((reason) => reason._id === reasonId)?.name;
        if (effectivePresence === 'absent' && keepMission) {
          updateMissionStatusLocal(objID, {
            user_assigned: null,
            user: null,
            isAssigned: false,
          });
        } else if (showedUp) {
          updateMissionStatusLocal(objID, { status: 'interrupted', reason: reasonName });
        } else {
          updateMissionStatusLocal(objID, {
            status: 'canceled',
            reason: reasonName,
            ...(effectivePresence === 'absent' ? { user_assigned: null } : {}),
          });
        }
        setModalDataReturn('mission_status_updated');
        toast.success(data.success);
        setIsSubmitting(false);
        toggle();
      },
      (err) => {
        toast.error(cancellationErrorMessage(err.response?.data?.message));
        setIsSubmitting(false);
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Annuler la mission
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-500">
            Ces modifications prendront effet à la confirmation et seront visibles côté
            entreprise et intérimaire.
          </p>
          {isWaiting ? (
            <div className="flex flex-col gap-3 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-20" />
              <div className="h-10 bg-gray-200 rounded w-full" />
            </div>
          ) : (
            <>
              {/* A breadcrumb of one step names where you are without offering
                  anywhere to go: on a mission nobody is on, « Annulation » is
                  the whole modal. */}
              {stepPills.length > 1 && (
                <StepPills steps={stepPills} current={currentStep} onSelect={setStep} />
              )}

              {currentStep === 'Annulation' && (
                <div className="flex flex-col gap-3">
                  <StyledInput
                    type="select"
                    label="À la demande"
                    placeholder="Sélectionner"
                    value={requester}
                    onChange={(e) => setRequester(e.target.value)}
                    list={offeredRequesters}
                  />
                  <StyledInput
                    type="select"
                    label="Raison"
                    placeholder="Sélectionner"
                    value={reasonId}
                    onChange={(e) => setReasonId(e.target.value)}
                    list={offeredReasons.map((reason) => ({
                      value: reason._id,
                      label: reason.name,
                    }))}
                  />
                  <StyledInput
                    type="textarea"
                    label="Commentaire (optionnel)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>
              )}

              {currentStep === 'Présence' && (
                <div className="flex flex-col gap-3">
                  {presenceAsked && (
                    <div className="flex flex-col gap-2">
                      <span className="ft-sm text-gray-500">
                        L'intérimaire s'est présenté en mission ?
                      </span>
                      <div className="flex gap-2">
                        {PRESENCE_OPTIONS.map(({ value, label, Icon }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setPresence(value)}
                            className={`flex-1 flex items-center justify-center gap-2 rounded-md py-4 px-3 ft-sm font-medium transition-colors ${
                              presence === value
                                ? 'border border-yellow-500 bg-yellow-50 text-yellow-500'
                                : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Icon wh={16} color={presence === value ? '#FF9600' : '#374151'} />
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {effectivePresence === 'absent' && (
                    <CheckboxOptionCard
                      id="mission-cancellation-keep-mission"
                      checked={keepMission}
                      onChange={(e) => setKeepMission(e.target.checked)}
                      title="Conserver la mission"
                      description="L'intérimaire sera dés-attribué de la mission"
                    />
                  )}

                  {effectivePresence === 'present' && (
                    <>
                      <DateField
                        label="Date d'interruption de la mission"
                        value={effectiveInterruptionDate}
                        minDate={boundary(mission?.duration?.datestart)}
                        maxDate={boundary(mission?.duration?.dateend)}
                        onChange={setInterruptionDate}
                        error={dateError}
                      />
                      <CheckboxOptionCard
                        id="mission-cancellation-replace"
                        checked={replace}
                        onChange={(e) => setReplace(e.target.checked)}
                        title="Remplacer l'intérimaire"
                        description="Crée une nouvelle mission identique à celle-ci pour y attribuer un autre intérimaire."
                      />
                    </>
                  )}
                </div>
              )}

              {currentStep === 'Dates' && (
                <div className="flex flex-col gap-3">
                  <DateField
                    label="Date de début"
                    value={effectiveDuplicateStart}
                    onChange={setDuplicateDateStart}
                    // The replacement covers the day the mission stops, never
                    // before: the earlier days were selectable and refused
                    // afterwards, which is the same answer given too late.
                    minDate={effectiveInterruptionDate}
                    error={dupErrors.start}
                  />
                  <DateField
                    label="Date de fin"
                    value={effectiveDuplicateEnd}
                    onChange={setDuplicateDateEnd}
                    minDate={effectiveDuplicateStart}
                    error={dupErrors.end}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {isSubmitting ? (
          <>
            {/* The form is in flight: closing underneath it would unmount the
                modal from under its own success/failure callback. */}
            <Button size="LG" color="DISABLED">
              Annuler
            </Button>
            <Button size="LG" color="DISABLED">
              Confirmation ...
            </Button>
          </>
        ) : (
          <>
            <Button size="LG" color="SECONDARY" onClick={toggle}>
              Annuler
            </Button>
            <Button
              size="LG"
              color={ready ? 'PRIMARY' : 'DISABLED'}
              onClick={ready ? (isLastStep ? handleConfirm : goNext) : undefined}
            >
              {isLastStep ? 'Confirmer' : 'Suivant'}
            </Button>
          </>
        )}
      </ModalFooter>
    </>
  );
}
